/**
 * Import Validation Configurations
 *
 * This file contains validation configurations for all import pages.
 * Each config defines validation rules for fields in a declarative way.
 */

export interface FieldValidation {
    required?: boolean;
    type?: 'number' | 'integer' | 'date' | 'enum';
    enumValues?: string[];
    customValidator?: (value: any) => string | null; // returns error message or null if valid
}

export interface ImportValidationConfig {
    [fieldName: string]: FieldValidation;
}

// ============================================================================
// Currency Rate Import Validation
// ============================================================================
export const CurrencyRateImportValidation: ImportValidationConfig = {
    currencyCodeFrom: { required: true },
    currencyCodeTo: { required: true },
    exchangeRate: { required: true, type: 'number' },
    dateEffectiveOn: { required: true, type: 'date' }
};

// ============================================================================
// Product Cost Import Validation
// ============================================================================
export const ProductCostImportValidation: ImportValidationConfig = {
    sku: { required: true },
    productCostAmount: { required: true, type: 'number' },
    dateEffectiveOn: { required: true, type: 'date' }
};

// ============================================================================
// Estimated Shipping Fee Import Validation
// ============================================================================
export const EstimatedShippingFeeImportValidation: ImportValidationConfig = {
    sku: { required: true },
    estimatedShippingFeeAmount: { required: true, type: 'number' },
    dateEffectiveOn: { required: true, type: 'date' }
};

// ============================================================================
// Product Import Validation (Product + Cost + Shipping Fee)
// ============================================================================
export const ProductImportValidation: ImportValidationConfig = {
    // Product fields
    sku: { required: true },
    // Product cost fields
    productCostAmount: { required: true, type: 'number' },
    // Estimated shipping fee fields
    estimatedShippingFeeAmount: { required: true, type: 'number' }
};

// ============================================================================
// Payment Import Validation
// ============================================================================
export const PaymentImportValidation: ImportValidationConfig = {
    orderNumber: { required: true },
    marketplaceCode: { required: true },
    paymentType: {
        required: true,
        type: 'enum',
        enumValues: [
            'ORDER_PAYMENT_BUYER',
            'COMMISSION_PAYMENT_SELLER',
            'TRANSACTION_FEE_PAYMENT_SELLER',
            'PRODUCT_COST_PAYMENT_SELLER',
            'SHIPPING_FEE_PAYMENT_SELLER',
            'OTHER_PAYMENT_SELLER',
            'OTHER_PAYMENT_BUYER'
        ]
    },
    paymentAmount: { required: true, type: 'number' },
    paymentCurrencyCode: { required: true },
    datePaymentOn: { required: true, type: 'date' },
    description: { required: true }
};

// ============================================================================
// Marketplace Order Import Validation
// ============================================================================
export const MarketplaceOrderImportValidation: ImportValidationConfig = {
    dateOrderOn: { required: true, type: 'date' },
    orderNumber: { required: true },
    marketplaceCode: { required: true },
    orderAmount: { required: true, type: 'number' },
    orderCurrencyCode: { required: true },
    commissionPercentage: { required: true, type: 'number' },
    transactionFee: { required: true, type: 'number' },
    sku: { required: true },
    quantity: { required: true, type: 'integer' },
    itemUnitPriceAmount: { required: true, type: 'number' }
};

// ============================================================================
// Marketplace Order Item Import Validation
// ============================================================================
export const MarketplaceOrderItemImportValidation: ImportValidationConfig = {
    orderNumber: { required: true },
    marketplaceCode: { required: true },
    sku: { required: true },
    quantity: { required: true, type: 'integer' },
    itemUnitPriceAmount: { required: true, type: 'number' }
};

// ============================================================================
// Shipping Fee Import Validation
// ============================================================================
export const ShippingFeeImportValidation: ImportValidationConfig = {
    orderNumber: { required: true },
    marketplaceCode: { required: true },
    shippingFeeAmount: { required: true, type: 'number' }
};

// ============================================================================
// Refund Import Validation
// ============================================================================
export const RefundImportValidation: ImportValidationConfig = {
    orderNumber: { required: true },
    marketplaceCode: { required: true },
    refundType: {
        required: true,
        type: 'enum',
        enumValues: [
            'ORDER_REFUND_BUYER',
            'COMMISSION_REFUND_SELLER',
            'TRANSACTION_FEE_REFUND_SELLER',
            'PRODUCT_COST_REFUND_SELLER',
            'SHIPPING_FEE_REFUND_SELLER',
            'OTHER_REFUND_SELLER',
            'OTHER_REFUND_BUYER'
        ]
    },
    refundAmount: { required: true, type: 'number' },
    refundCurrencyCode: { required: true },
    dateRefundOn: { required: true, type: 'date' },
    description: { required: true }
};
